import Service from "@stringf/services/auth";
import { IUser } from "@stringf/services/types";
import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";

export interface AccountInfo {
  account?: IUser;
  loading: boolean;
}

export interface AccountObj {
  [key: string]: AccountInfo | undefined;
}

export const accountsAtom = atomWithStorage<AccountObj>("accounts", {});

const pendingRequests: { [key: string]: Promise<AccountInfo> } = {};

/** Forgets lookups in flight, so the next account's first lookup is its own. */
export const clearPendingAccountRequests = () => {
  for (const userId of Object.keys(pendingRequests)) delete pendingRequests[userId];
};

export const setAccountAtom = atom(
  null,
  async (get, set, userId: string, reload?: boolean) => {
    const old = get(accountsAtom);
    if (!reload && old[userId] !== undefined && old[userId].loading)
      return old[userId];

    if (reload || !pendingRequests[userId]) {
      pendingRequests[userId] = (async () => {
        if (old[userId] !== undefined) {
          set(accountsAtom, { ...old, [userId]: { account: old[userId]?.account, loading: true } });
        }

        try {
          const lookup = await Service.lookupAccount(userId);
          if (!lookup.account?.id) {
            delete pendingRequests[userId];
            return { account: undefined, loading: false };
          }

          const accountId = lookup.account!.id as string;
          set(accountsAtom, (prev) => ({
            ...prev,
            [accountId]: { account: lookup.account, loading: false },
          }));
          delete pendingRequests[userId];
          return { account: lookup.account, loading: false } as AccountInfo;
        } catch (e) {
          delete pendingRequests[userId];
          throw e;
        }
      })();
    }
    return pendingRequests[userId];
  }
);
